import React, { useState, useEffect } from 'react';
import styles from '../../styles/GestionUsuariosStyles/GestionUsuarios.module.css';
import { getInstitutions } from '../../api/institutions';
import { getCampus } from '../../api/campus';
import { getSecciones } from '../../api/secciones';
import { getRoles } from '../../api/roles';
import { getRolesUsuario, asignarRolUsuario, revocarRolUsuario } from '../../api/usuarios';

function UserModal({ isOpen, onClose, onSave, editMode, userData }) {
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    usuario: '',
    contrasena: '',
    emailInstitucional: '',
    id_rol: '',
    institucion: '',
    estado: 'ACTIVO',
    campusAsignados: [],
    seccionesAsignadas: [],
    fechaCreacion: new Date().toISOString().split('T')[0],
    ultimoAcceso: ''
  });

  const [instituciones, setInstituciones] = useState([]);
  const [campusList, setCampusList] = useState([]);
  const [seccionesList, setSeccionesList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [rolesAdicionales, setRolesAdicionales] = useState([]);
  const [rolParaAgregar, setRolParaAgregar] = useState('');

  // Cargar roles adicionales del usuario en modo edición.
  // Sincroniza el campo "Rol *" (id_rol) con el rol marcado como Principal
  // en sys_usuario_roles para evitar la desincronización con el campo legacy
  // sys_usuarios.id_rol.
  useEffect(() => {
    if (editMode && userData?.id_usuario && isOpen) {
      getRolesUsuario(userData.id_usuario).then(roles => {
        const lista = Array.isArray(roles) ? roles : [];
        setRolesAdicionales(lista);
        const principal = lista.find(r => r.es_rol_principal);
        if (principal && principal.id_rol) {
          setFormData(prev => (
            prev.id_rol === principal.id_rol ? prev : { ...prev, id_rol: principal.id_rol }
          ));
        }
      }).catch(() => setRolesAdicionales([]));
    } else {
      setRolesAdicionales([]);
    }
  }, [editMode, userData?.id_usuario, isOpen]);

  const handleAgregarRol = async () => {
    if (!rolParaAgregar || !userData?.id_usuario) return;
    try {
      await asignarRolUsuario(userData.id_usuario, Number(rolParaAgregar), false);
      const updated = await getRolesUsuario(userData.id_usuario);
      setRolesAdicionales(Array.isArray(updated) ? updated : []);
      setRolParaAgregar('');
    } catch (err) {
        console.error('[handleAgregarRol] Error del servidor:', err.message);
        alert(`Error al asignar rol: ${err.message || 'Error desconocido'}`);
    }
  };

  const handleRevocarRol = async (idUsuarioRol) => {
    if (!window.confirm('¿Revocar este rol del usuario?')) return;
    try {
      await revocarRolUsuario(idUsuarioRol);
      const updated = await getRolesUsuario(userData.id_usuario);
      setRolesAdicionales(Array.isArray(updated) ? updated : []);
    } catch (err) {
      alert(err.message || 'Error al revocar rol');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [instData, campData, seccData, rolesData] = await Promise.all([
          getInstitutions(),
          getCampus(),
          getSecciones(),
          getRoles()
        ]);
        setInstituciones(instData || []);
        setCampusList(campData || []);
        setSeccionesList(seccData || []);
        setRolesList(rolesData || []);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoadingData(false);
      }
    };
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (editMode && userData) {
      setFormData({
        nombre: userData.nombre || '',
        apellidos: userData.apellidos || '',
        usuario: userData.usuario || '',
        contrasena: '',
        emailInstitucional: userData.email_institucional || userData.email || '',
        id_rol: userData.id_rol || '',
        institucion: userData.id_institucion || '',
        estado: userData.activo !== undefined ? (userData.activo ? 'ACTIVO' : 'INACTIVO') : (userData.estado || 'ACTIVO'),
        campusAsignados: userData.campus_asignados || [],
        seccionesAsignadas: userData.secciones_asignadas || [],
        fechaCreacion: userData.fecha_creacion || '',
        ultimoAcceso: userData.ultimo_acceso || ''
      });
    } else if (!editMode) {
      // Resetear formulario para nuevo usuario
      setFormData({
        nombre: '',
        apellidos: '',
        usuario: '',
        contrasena: '',
        emailInstitucional: '',
        id_rol: '',
        institucion: '',
        estado: 'ACTIVO',
        campusAsignados: [],
        seccionesAsignadas: [],
        fechaCreacion: new Date().toISOString().split('T')[0],
        ultimoAcceso: ''
      });
    }
  }, [editMode, userData, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCampusToggle = (campusId) => {
    setFormData(prev => ({
      ...prev,
      campusAsignados: prev.campusAsignados.includes(campusId)
        ? prev.campusAsignados.filter(id => id !== campusId)
        : [...prev.campusAsignados, campusId]
    }));
  };

  const handleSeccionToggle = (seccionId) => {
    setFormData(prev => ({
      ...prev,
      seccionesAsignadas: prev.seccionesAsignadas.includes(seccionId)
        ? prev.seccionesAsignadas.filter(id => id !== seccionId)
        : [...prev.seccionesAsignadas, seccionId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.nombre || !formData.usuario || !formData.id_rol || !formData.institucion) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }

    if (!editMode && !formData.contrasena) {
      alert('La contraseña es obligatoria para nuevos usuarios');
      return;
    }

    // Obtener el nombre del rol seleccionado
    const rolSeleccionado = rolesList.find(r => r.id_rol === Number(formData.id_rol));

    const payload = {
      nombre_completo: `${formData.nombre} ${formData.apellidos}`.trim(),
      usuario: formData.usuario,
      email: formData.emailInstitucional,
      id_rol: Number(formData.id_rol),
      id_institucion: Number(formData.institucion),
      activo: formData.estado === 'ACTIVO',
      campus_ids: formData.campusAsignados,
      seccion_ids: formData.seccionesAsignadas
    };

    // Solo incluir contraseña si no es modo edición o si se proporcionó una nueva
    if (!editMode && formData.contrasena) {
      payload.password = formData.contrasena;
    } else if (editMode && formData.contrasena) {
      payload.password = formData.contrasena;
    }

    onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{editMode ? 'Editar Usuario' : 'Registro de Usuario'}</h2>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Nombre(s) *</label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Apellidos *</label>
              <input
                type="text"
                name="apellidos"
                value={formData.apellidos}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Usuario *</label>
              <input
                type="text"
                name="usuario"
                value={formData.usuario}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Contraseña {!editMode && '*'}</label>
              <div className={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="contrasena"
                  value={formData.contrasena}
                  onChange={handleChange}
                  required={!editMode}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Email Institucional</label>
              <input
                type="email"
                name="emailInstitucional"
                value={formData.emailInstitucional}
                onChange={handleChange}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Rol *</label>
              <select
                name="id_rol"
                value={formData.id_rol}
                onChange={handleChange}
                required
              >
                <option value="">-- Selecciona un rol --</option>
                {rolesList.map(rol => (
                  <option key={rol.id_rol} value={rol.id_rol}>
                    {rol.nombre_rol}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Institución *</label>
              <select
                name="institucion"
                value={formData.institucion}
                onChange={handleChange}
                required
              >
                <option value="">-- Selecciona una institución --</option>
                {instituciones.map(inst => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name || inst.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Estado</label>
              <select
                name="estado"
                value={formData.estado}
                onChange={handleChange}
              >
                <option value="ACTIVO">ACTIVO</option>
                <option value="INACTIVO">INACTIVO</option>
              </select>
            </div>
          </div>

          <div className={styles.formSection}>
            <label>Campus asignado(s)</label>
            <div className={styles.chipsContainer}>
              {campusList.map(campus => (
                <button
                  key={campus.id}
                  type="button"
                  className={`${styles.chip} ${formData.campusAsignados.includes(campus.id) ? styles.chipActive : ''}`}
                  onClick={() => handleCampusToggle(campus.id)}
                >
                  {campus.nombre}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formSection}>
            <label>Secciones Asignadas</label>
            <div className={styles.chipsContainer}>
              {seccionesList.map(seccion => (
                <button
                  key={seccion.id}
                  type="button"
                  className={`${styles.chip} ${formData.seccionesAsignadas.includes(seccion.id) ? styles.chipActive : ''}`}
                  onClick={() => handleSeccionToggle(seccion.id)}
                >
                  {seccion.nombre}
                </button>
              ))}
            </div>
          </div>

          {/* Sección de Roles Adicionales (solo en modo edición) */}
          {editMode && userData?.id_usuario && (
            <div className={styles.formSection}>
              <label>Roles Asignados</label>
              <div style={{ marginBottom: '8px' }}>
                {rolesAdicionales.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {rolesAdicionales.map(ur => (
                      <div key={ur.id_usuario_rol} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '6px 12px', background: ur.es_rol_principal ? '#e3f2fd' : '#f5f5f5',
                        borderRadius: '20px', fontSize: '13px', border: ur.es_rol_principal ? '1px solid #1976d2' : '1px solid #ddd'
                      }}>
                        <span>{ur.rol_nombre}</span>
                        {ur.es_rol_principal && <span style={{ fontSize: '11px', color: '#1976d2', fontWeight: 600 }}>(Principal)</span>}
                        {!ur.es_rol_principal && (
                          <button
                            type="button"
                            onClick={() => handleRevocarRol(ur.id_usuario_rol)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53935', fontSize: '16px', lineHeight: 1, padding: 0 }}
                            title="Revocar rol"
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span style={{ fontSize: '13px', color: '#888' }}>Sin roles adicionales asignados</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={rolParaAgregar}
                  onChange={(e) => setRolParaAgregar(e.target.value)}
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                >
                  <option value="">-- Agregar rol adicional --</option>
                  {rolesList
                    .filter(r => !rolesAdicionales.some(ur => ur.id_rol === r.id_rol))
                    .map(rol => (
                      <option key={rol.id_rol} value={rol.id_rol}>{rol.nombre_rol}</option>
                    ))
                  }
                </select>
                <button
                  type="button"
                  onClick={handleAgregarRol}
                  disabled={!rolParaAgregar}
                  style={{
                    padding: '8px 16px', background: rolParaAgregar ? '#1976d2' : '#ccc',
                    color: '#fff', border: 'none', borderRadius: '6px', cursor: rolParaAgregar ? 'pointer' : 'default',
                    fontSize: '13px', fontWeight: 500
                  }}
                >
                  Agregar
                </button>
              </div>
            </div>
          )}

          {editMode && (
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>Fecha de creación</label>
                <input
                  type="text"
                  value={formData.fechaCreacion}
                  disabled
                  className={styles.disabledInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Último acceso</label>
                <input
                  type="text"
                  value={formData.ultimoAcceso || 'Nunca'}
                  disabled
                  className={styles.disabledInput}
                />
              </div>
            </div>
          )}

          <div className={styles.modalActions}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              CANCELAR
            </button>
            <button type="submit" className={styles.saveButton}>
              GUARDAR
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserModal;
